import { Play } from "lucide-react";
import { useRef, useState, type ReactNode } from "react";

import { useProjectOverviewQuery } from "@/api/hooks";
import type { PreviewManualSessionState } from "@/api/previewManual";
import {
  usePreviewCandidatesQuery,
  usePreviewPatientsQuery,
} from "@/api/preview";
import {
  classifyReplayFailure,
  useReplayPreviewRunMutation,
  type ReplayFailure,
} from "@/api/previewReplay";
import { useCapability } from "@/auth/SessionContext";
import { patientForLanguage, recallRun, rememberRun } from "@/lib/labRunMemory";
import {
  PREVIEW_REPLAY_MAX_TURNS,
  replayRequestBody,
} from "@/lib/previewReplay";
import { SIM_LANGUAGE_LABELS, type SimLanguage } from "@/lib/previewSim";
import { Button } from "@/ui/Button";
import { Dialog, DialogClose, DialogContent } from "@/ui/Dialog";
import { Field, Select } from "@/ui/Input";

import { LabPatientField } from "./LabPatientField";

const FAILURE_MESSAGES = {
  stale_target:
    "This project's preview target changed since you opened this page. Reload, then try again.",
  duplicate_command:
    "That request was already used for a different command. Try again to start a new one.",
  admission_refused:
    "The preview target refused this testing version's safety posture, so nothing was replayed.",
  preview_target:
    "This project's preview target is not available for previews right now.",
  candidate_unusable:
    "The testing version chosen in the top bar is not available or has expired. Choose another one.",
  candidate_validator_mismatch:
    "This testing version was validated by another engine build and must be rebuilt first.",
  patient_gone:
    "The preview target no longer offers that synthetic patient. Choose again.",
  invalid_turn: `The preview target replays at most ${PREVIEW_REPLAY_MAX_TURNS} turns.`,
  source_unavailable:
    "This run is not available to continue from for your account.",
  denied: "Your account cannot start preview sessions here.",
  unauthenticated: "Your sign-in has expired. Sign in again and retry.",
  unavailable: "This deployment does not serve replays.",
  unknown_dispatch:
    "The service did not confirm whether the session started. Retry: the same request cannot start a second one.",
  validator_pin_unavailable:
    "This deployment cannot establish its engine build, so nothing was replayed.",
  error: "The session could not be started.",
} as const satisfies Record<ReplayFailure["kind"], string>;

/**
 * "Continue from turn N": a new manual session that replays this run's user
 * inputs through turn N and then waits for the clinician. Nothing of the source
 * run's state is restored (ADR 0013): Liv answers every replayed line afresh.
 * Uses the testing version chosen in the top bar and the synthetic patient
 * chosen among those the target offers in the language, then opens the new run.
 */
export function LabContinueFromTurn({
  projectId,
  previewRunRef,
  turnNumber,
  candidateId,
  onContinued,
  label = `Continue from turn ${turnNumber}`,
  note,
  fixedLanguage = null,
  quick = false,
  dialog = false,
}: {
  projectId: string;
  previewRunRef: string;
  turnNumber: number;
  candidateId: string | null;
  onContinued: (previewRunRef: string) => void;
  label?: string;
  note?: ReactNode;
  /** The only language offered (Undo keeps the run's own). */
  fixedLanguage?: string | null;
  /** One click starts at once when the source run's patient is known; otherwise the form opens. */
  quick?: boolean;
  /** A "Continue from here" pill that confirms in a dialog; the patient is asked only when unknown. */
  dialog?: boolean;
}) {
  const create = useCapability("preview.create");
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const patients = usePreviewPatientsQuery(projectId);
  const replay = useReplayPreviewRunMutation(projectId, previewRunRef);
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState<string | null>(null);
  // The patient this browser started the source run with; unknown means the tester chooses.
  const [patientRef, setPatientRef] = useState(
    () => recallRun(previewRunRef).patientRef ?? "",
  );
  const [recalledPatientRef] = useState(patientRef);
  const idempotencyKey = useRef<string>(crypto.randomUUID());

  const items = patients.data?.items ?? [];
  const languages =
    fixedLanguage !== null
      ? [fixedLanguage]
      : [...new Set(items.map((item) => item.language))];
  const known = items.find((item) => item.ref === patientRef);
  const chosenLanguage =
    fixedLanguage ?? language ?? known?.language ?? languages[0] ?? null;
  const offered = items.filter((item) => item.language === chosenLanguage);
  // Never the first of the list by default: a wrong patient would silently change the conversation.
  const patient = offered.find((item) => item.ref === patientRef);
  const candidate =
    candidates.data?.items.find((item) => item.candidateId === candidateId) ??
    null;
  const revision = overview.data?.project.previewTargetRevision;
  const failure = replay.isError ? classifyReplayFailure(replay.error) : null;
  const askPatient =
    recalledPatientRef === "" ||
    (patients.isSuccess &&
      !items.some((item) => item.ref === recalledPatientRef));

  const blocker = !create.allowed
    ? `${create.reason} Continuing needs the "preview.create" permission.`
    : turnNumber > PREVIEW_REPLAY_MAX_TURNS
      ? `The preview target replays at most ${PREVIEW_REPLAY_MAX_TURNS} turns.`
      : candidate === null
        ? "Choose a testing version in the top bar."
        : candidate.validity?.state === "expired"
          ? "This testing version has expired. Choose another one in the top bar."
          : patient === undefined
            ? patients.isPending
              ? "Loading the synthetic patients…"
              : offered.length === 0
                ? "The preview target offers no synthetic patient."
                : "Choose the synthetic patient."
            : revision === undefined
              ? "Loading the project…"
              : null;

  function start() {
    if (
      blocker !== null ||
      candidate === null ||
      patient === undefined ||
      revision === undefined
    ) {
      return;
    }
    replay.mutate(
      {
        ...replayRequestBody({
          candidateId: candidate.candidateId,
          patientRef: patient.ref,
          expectedPreviewTargetRevision: revision,
          idempotencyKey: idempotencyKey.current,
          boundary: { kind: "through_turn", turnNumber },
        }),
        continueManually: true,
      },
      {
        onSuccess(created) {
          idempotencyKey.current = crypto.randomUUID();
          setOpen(false);
          rememberRun(created.previewRunRef, {
            patientRef: patient.ref,
            restoreThrough: turnNumber,
          });
          onContinued(created.previewRunRef);
        },
        // A one-click start has no form open to show why it failed.
        onError: () => setOpen(true),
      },
    );
  }

  function changed() {
    idempotencyKey.current = crypto.randomUUID();
    replay.reset();
  }

  const progress = replay.isPending
    ? "Starting…"
    : failure?.kind === "unknown_dispatch"
      ? "Retry"
      : null;
  const messages = (
    <>
      {blocker !== null ? (
        <p className="text-sm text-muted-foreground">{blocker}</p>
      ) : null}
      {failure !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {FAILURE_MESSAGES[failure.kind]}
        </p>
      ) : null}
    </>
  );
  const chooser = (
    <>
      <Field label="Synthetic patient language">
        {({ inputId }) => (
          <Select
            id={inputId}
            value={chosenLanguage ?? ""}
            onChange={(event) => {
              const next = event.target.value;
              setLanguage(next);
              // The same gender in the new language when there is one; otherwise the tester chooses.
              setPatientRef(
                patientForLanguage(items, patient, next)?.ref ?? "",
              );
              changed();
            }}
          >
            {languages.map((code) => (
              <option key={code} value={code}>
                {SIM_LANGUAGE_LABELS[code as SimLanguage] ?? code}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <LabPatientField
        patients={offered}
        value={patient?.ref ?? ""}
        placeholder="Choose a patient"
        onChange={(ref) => {
          setPatientRef(ref);
          changed();
        }}
      />
    </>
  );

  if (dialog) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => (replay.isPending ? undefined : setOpen(next))}
      >
        <Button
          variant="outline"
          size="sm"
          className="h-6 rounded-full border-primary px-2 text-xs text-primary"
          disabled={!create.allowed}
          title={!create.allowed ? (blocker ?? undefined) : undefined}
          onClick={() => setOpen(true)}
        >
          <Play aria-hidden="true" className="h-3 w-3" />
          Continue from here
          <span className="sr-only"> (turn {turnNumber})</span>
        </Button>
        <DialogContent
          title={`Continue from turn ${turnNumber}?`}
          description={`A new conversation starts with the patient's lines through turn ${turnNumber}. Liv answers them again with the testing version in the top bar, so her replies can differ.`}
          busy={replay.isPending}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                disabled={blocker !== null || replay.isPending}
                title={blocker ?? undefined}
                onClick={start}
              >
                {progress ?? "Continue"}
              </Button>
            </>
          }
        >
          {askPatient || blocker !== null || failure !== null ? (
            <div className="space-y-2">
              {askPatient ? chooser : null}
              {messages}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="space-y-2">
      <Button
        variant={quick ? "primary" : "outline"}
        size={quick ? "md" : "sm"}
        aria-expanded={open}
        disabled={!create.allowed}
        title={!create.allowed ? (blocker ?? undefined) : undefined}
        onClick={() =>
          quick && blocker === null && patient !== undefined
            ? start()
            : setOpen(!open)
        }
      >
        {label}
      </Button>
      {open ? (
        <form
          aria-label={label}
          className="space-y-2 rounded-md border border-border p-3"
          onSubmit={(event) => {
            event.preventDefault();
            start();
          }}
        >
          <p className="text-sm text-muted-foreground">
            {note ?? (
              <>
                Starts a new manual session that replays this run&apos;s patient
                lines through turn {turnNumber}, then waits for you. Liv answers
                every line afresh; nothing of this run is restored.
              </>
            )}
          </p>
          {chooser}
          <Button
            type="submit"
            disabled={blocker !== null || replay.isPending}
            title={blocker ?? undefined}
          >
            {progress ?? "Continue manually"}
          </Button>
          {messages}
        </form>
      ) : null}
    </div>
  );
}

/** With a single turn there is nothing to keep: a new session is the undo. */
export function LabUndoLastMessage({
  manual,
  ...rest
}: {
  projectId: string;
  previewRunRef: string;
  manual: PreviewManualSessionState;
  candidateId: string | null;
  onContinued: (previewRunRef: string) => void;
}) {
  const keep = manual.turnsCompleted - 1;
  // Busy: a line or replay is still in flight, so the count is not yet the last line.
  if (keep < 1 || manual.status === "busy") {
    return null;
  }
  return (
    <LabContinueFromTurn
      {...rest}
      turnNumber={keep}
      label="Undo"
      fixedLanguage={manual.language ?? null}
      note={
        <>
          Starts a new manual session without your last line: your lines through
          turn {keep} are replayed and Liv answers each one again, so Liv&apos;s
          earlier replies are regenerated and may differ.
        </>
      }
    />
  );
}
